<script setup>
import { computed } from 'vue';

const props = defineProps({
    dimensions: {
        type: Object,
        default: () => ({}),
    },
    modelValue: {
        type: Array,
        default: () => [],
    },
    idPrefix: {
        type: String,
        default: 'dimpick',
    },
});

const emit = defineEmits(['update:modelValue']);

const dimensionGroups = [
    {
        label: 'Indices',
        keys: ['getMainTPI', 'VI', 'AI', 'HI', 'II'],
        labelIsCheckbox: true,
    },
    {
        label: 'Indices (Prozent)',
        keys: ['getMainTPIPercent', 'getVIPercent', 'getAIPercent', 'getHIPercent', 'getIIPercent'],
        labelIsCheckbox: true,
    },
    {
        label: 'Substantive',
        keys: ['getSubstantivePercentage', 'getAbstractPercentage'],
        labelIsCheckbox: true,
    },
    {
        label: 'Persönliche Ansprache',
        keys: ['getPersonalSentencePercentage', 'you_percentage', 'we_percentage'],
        labelIsCheckbox: true,
    },
    {
        label: 'Impulse',
        keys: ['getImpulsWeightPerSentence'],
        labelIsCheckbox: false,
    },
    {
        label: 'Allgemein',
        keys: ['sentence_length', 'word_length_syll', 'word_num'],
        labelIsCheckbox: false,
    },
];

const visibleGroups = computed(() =>
    dimensionGroups
        .map(group => ({
            label: group.label,
            labelIsCheckbox: group.labelIsCheckbox,
            items: group.keys
                .filter(k => k in props.dimensions)
                .map(k => ({ key: k, label: props.dimensions[k] })),
        }))
        .filter(group => group.items.length > 0)
);

function toggle(key) {
    const current = [...props.modelValue];
    const idx = current.indexOf(key);
    if (idx >= 0) {
        current.splice(idx, 1);
    } else {
        current.push(key);
    }
    emit('update:modelValue', current);
}

function toggleGroup(group) {
    const current = [...props.modelValue];
    const allSelected = group.items.every(item => current.includes(item.key));

    if (allSelected) {
        group.items.forEach(item => {
            const idx = current.indexOf(item.key);
            if (idx >= 0) current.splice(idx, 1);
        });
    } else {
        group.items.forEach(item => {
            if (!current.includes(item.key)) current.push(item.key);
        });
    }
    emit('update:modelValue', current);
}

function isGroupAllSelected(group) {
    return group.items.every(item => props.modelValue.includes(item.key));
}

function isGroupPartial(group) {
    const some = group.items.some(item => props.modelValue.includes(item.key));
    return some && !isGroupAllSelected(group);
}
</script>

<template>
    <div>
        <strong>Dimensionen</strong>
        <div class="d-flex flex-wrap mt-2" style="gap: 0.75rem;">
            <div v-for="group in visibleGroups" :key="group.label" class="card">
                <div class="card-body py-2 px-3">
                    <div v-if="group.labelIsCheckbox" class="form-check mb-1">
                        <input class="form-check-input" type="checkbox" :id="idPrefix + '-group-' + group.label"
                            :checked="isGroupAllSelected(group)" :indeterminate="isGroupPartial(group)"
                            @change="toggleGroup(group)" />
                        <label class="form-check-label" :for="idPrefix + '-group-' + group.label">
                            <strong>{{ group.label }}</strong>
                        </label>
                    </div>
                    <strong v-else class="d-block mb-1">{{ group.label }}</strong>
                    <div v-for="item in group.items" :key="item.key" class="form-check" :class="{ 'ml-3': group.labelIsCheckbox }">
                        <input class="form-check-input" type="checkbox" :id="idPrefix + '-' + item.key"
                            :checked="modelValue.includes(item.key)" @change="toggle(item.key)" />
                        <label class="form-check-label" :for="idPrefix + '-' + item.key">{{ item.label }}</label>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
